import { useState } from 'react'
import { X, Truck, Loader2, MapPin } from 'lucide-react'
import { useUIStore } from '@/stores/uiStore'
import { useSessionStore } from '@/stores/sessionStore'
import {
  useDeliveryRuns,
  useDeliveryRunDrivers,
  useCreateDeliveryRun,
  useAddOrdersToRun,
  buildRunName,
} from '@/api/deliveryRuns'

interface AddToDeliveryRunModalProps {
  // The selected delivery orders (rows from the search payload). Only `id`
  // and a couple of display fields are read.
  orders: Array<{
    id: number
    number?: string
    customer?: { display_name?: string } | null
    address?: string
  }>
  onClose: () => void
  // Called after orders are successfully added so the parent can clear the
  // selection and refetch the Deliveries list.
  onAdded: () => void
}

export function AddToDeliveryRunModal({
  orders,
  onClose,
  onAdded,
}: AddToDeliveryRunModalProps) {
  const { showToast } = useUIStore()
  const { selectedStore } = useSessionStore()

  // Only open runs can receive orders.
  const { data: openRuns, isLoading: loadingRuns } = useDeliveryRuns(['open'])
  const { data: drivers } = useDeliveryRunDrivers()
  const createRun = useCreateDeliveryRun()
  const addOrders = useAddOrdersToRun()

  // Default mode: "existing" once open runs have loaded, else "new". Derived
  // (rather than stored and set in an effect) so it tracks the async runs load
  // without a setState-in-effect; a non-null override is the cashier's
  // explicit toggle.
  const hasOpenRuns = (openRuns?.length ?? 0) > 0
  const [modeOverride, setModeOverride] = useState<'existing' | 'new' | null>(null)
  const mode = modeOverride ?? (hasOpenRuns ? 'existing' : 'new')
  const [selectedRunId, setSelectedRunId] = useState<number | null>(null)
  const [driverId, setDriverId] = useState<number | null>(null)

  // Reconcile the chosen run against the freshly-loaded list on every render:
  // if `selectedRunId` is no longer an open run (it was started/closed
  // elsewhere while this modal sat open), fall back to the first open run so
  // we never submit a stale id. The <select> binds to this and handleSubmit
  // reads it — no polling needed (useDeliveryRuns refetches on mount + focus).
  const effectiveRunId =
    openRuns?.some((r) => r.id === selectedRunId)
      ? selectedRunId
      : (openRuns?.[0]?.id ?? null)

  const receiptIds = orders.map((o) => o.id)
  const busy = createRun.isPending || addOrders.isPending
  const canSubmit =
    receiptIds.length > 0 &&
    !busy &&
    // New runs are auto-named, so the only requirement is having orders.
    (mode === 'existing' ? !!effectiveRunId : true)

  const handleSubmit = async () => {
    if (!canSubmit) return
    try {
      let runId = effectiveRunId
      if (mode === 'new') {
        const run = await createRun.mutateAsync({
          name: buildRunName(selectedStore?.id),
          driver_id: driverId,
        })
        runId = run.id
      }
      if (!runId) return

      const result = await addOrders.mutateAsync({ runId, receiptIds })
      const added = result.added?.length ?? 0
      const rejected = result.rejected?.length ?? 0
      showToast({
        type: rejected > 0 ? 'warning' : 'success',
        title: 'Added to delivery run',
        message:
          `${added} order${added === 1 ? '' : 's'} added` +
          (rejected > 0 ? ` · ${rejected} skipped (already assigned)` : ''),
      })
      onAdded()
      onClose()
    } catch (err) {
      showToast({
        type: 'error',
        title: 'Could not add to delivery run',
        message: err instanceof Error ? err.message : undefined,
      })
    }
  }

  return (
    <div className="fixed inset-0 z-60 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <Truck className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">
              Add to Delivery Run
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 rounded-md"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-4 max-h-[60vh] overflow-y-auto">
          {/* Selected orders summary */}
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">
              {receiptIds.length} order{receiptIds.length === 1 ? '' : 's'}{' '}
              selected
            </p>
            <div className="space-y-1.5 max-h-32 overflow-y-auto rounded-md border border-gray-100 bg-gray-50 p-2">
              {orders.map((o) => (
                <div
                  key={o.id}
                  className="flex items-center gap-2 text-sm text-gray-600"
                >
                  <span className="font-semibold text-gray-900">
                    #{o.number || o.id}
                  </span>
                  {o.customer?.display_name && (
                    <span className="truncate">{o.customer.display_name}</span>
                  )}
                  {o.address && (
                    <span className="flex items-center gap-1 text-gray-400 truncate">
                      <MapPin className="w-3 h-3 shrink-0" />
                      {o.address}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Mode toggle */}
          <div className="flex gap-2">
            <button
              onClick={() => setModeOverride('existing')}
              disabled={!hasOpenRuns}
              className={`flex-1 px-3 py-2 text-sm font-medium rounded-md border transition-colors disabled:opacity-40 ${
                mode === 'existing'
                  ? 'bg-blue-50 border-blue-300 text-blue-700'
                  : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              Existing run
            </button>
            <button
              onClick={() => setModeOverride('new')}
              className={`flex-1 px-3 py-2 text-sm font-medium rounded-md border transition-colors ${
                mode === 'new'
                  ? 'bg-blue-50 border-blue-300 text-blue-700'
                  : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              + New run
            </button>
          </div>

          {mode === 'existing' ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Delivery run
              </label>
              {loadingRuns ? (
                <p className="text-sm text-gray-400">Loading runs…</p>
              ) : hasOpenRuns ? (
                <select
                  value={effectiveRunId ?? ''}
                  onChange={(e) => setSelectedRunId(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {openRuns!.map((run) => (
                    <option key={run.id} value={run.id}>
                      {run.name} ({run.stop_count} stop
                      {run.stop_count === 1 ? '' : 's'}
                      {run.driver_name ? ` · ${run.driver_name}` : ''})
                    </option>
                  ))}
                </select>
              ) : (
                <p className="text-sm text-gray-400">
                  No open runs — create a new one.
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Run name
                </label>
                {/* Auto-named — the cashier doesn't set it. The exact name is
                    stamped at submit time; this preview may differ by a minute. */}
                <p className="px-3 py-2 text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-md">
                  {buildRunName(selectedStore?.id)}{' '}
                  <span className="text-gray-400">(auto)</span>
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Driver <span className="text-gray-400">(optional)</span>
                </label>
                <select
                  value={driverId ?? ''}
                  onChange={(e) =>
                    setDriverId(e.target.value ? Number(e.target.value) : null)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">— Unassigned —</option>
                  {drivers?.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50">
          <button
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            Add {receiptIds.length} order{receiptIds.length === 1 ? '' : 's'}
          </button>
        </div>
      </div>
    </div>
  )
}
